import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import { getArticles } from "../lib/api";
import { ArrowRight } from "lucide-react";
import { ZR_SERIES, ZR_BANDS, ZR_UI } from "../i18n/zwischenraum";

const Library = () => {
  const { t, lang } = useLang();
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    getArticles().then(setArticles).catch(() => {});
  }, []);

  const [featured, ...rest] = articles;

  return (
    <div data-testid="library-page" className="bg-obsidian">
      <Seo pageKey="library" />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-16 md:px-12 md:pt-52">
        <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.library.overline}</span>
        <h1 className="mt-6 max-w-3xl font-serif text-6xl font-light leading-[0.95] tracking-tight text-white md:text-8xl">
          <MaskedLines lines={[t.library.title]} startDelay={0.2} />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">{t.library.sub}</p>
        </Reveal>
      </section>

      {/* New series feature */}
      <section className="mx-auto max-w-[1400px] px-6 pb-16 md:px-12" data-testid="library-zwischenraum-feature">
        <Reveal>
          <Link to="/library/zwischenraum" className="group grid gap-10 border-y border-champagne/20 bg-obsidian-soft py-10 md:grid-cols-12 md:items-center">
            <div className="flex gap-3 md:col-span-5 md:pl-10">
              {ZR_BANDS.slice(0, 4).map((b, i) => (
                <div key={b.slug} className={`relative aspect-[3/4] w-1/4 overflow-hidden border border-white/10 transition-transform duration-500 ${i % 2 ? "translate-y-3" : ""} group-hover:-translate-y-1`}>
                  <img src={b.cover} alt="" loading="lazy" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
            <div className="md:col-span-6 md:col-start-7 md:pr-10">
              <span className="text-[11px] uppercase tracking-[0.3em] text-champagne">{(ZR_UI[lang] || ZR_UI.de).overline}</span>
              <h2 className="mt-3 font-serif text-3xl font-light leading-tight text-white transition-colors group-hover:text-champagne md:text-4xl">{ZR_SERIES.title}</h2>
              <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-white/55">{ZR_SERIES.claim}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80">
                {(ZR_UI[lang] || ZR_UI.de).discover} <ArrowRight size={14} className="text-champagne transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>
      </section>

      {featured && (
        <section className="mx-auto max-w-[1400px] px-6 pb-16 md:px-12">
          <Reveal>
            <Link to={`/library/${featured.slug}`} data-testid={`library-featured-${featured.slug}`} className="group grid gap-10 border-t border-white/10 pt-12 md:grid-cols-2">
              <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                <img src={featured.cover_image} alt={featured.title[lang]} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-[0.25em] text-champagne">{featured.read_time} {t.library.readTime}</span>
                <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-white transition-colors group-hover:text-champagne md:text-5xl">{featured.title[lang]}</h2>
                <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-white/55">{featured.excerpt[lang]}</p>
                <span className="mt-8 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80">
                  {t.library.read} <ArrowRight size={14} className="text-champagne transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      <section className="mx-auto max-w-[1400px] px-6 pb-40 md:px-12">
        <div className="grid gap-x-8 gap-y-16 border-t border-white/10 pt-16 md:grid-cols-3">
          {rest.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.1}>
              <Link to={`/library/${a.slug}`} data-testid={`library-article-${a.slug}`} className="group block">
                <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                  <img src={a.cover_image} alt={a.title[lang]} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <span className="mt-5 block text-[10px] uppercase tracking-[0.25em] text-champagne">{a.read_time} {t.library.readTime}</span>
                <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-white transition-colors group-hover:text-champagne">{a.title[lang]}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/50">{a.excerpt[lang]}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <FaqSection pageKey="library" />
    </div>
  );
};

export default Library;
